/**
 * Лобби: категории столов (взнос N), открытые столы и их заполненность, краткая сводка
 * профиля (история и статистика — в профиле), проверка файла. Сведения о площадке (режим,
 * источник данных, счёт банка) — только в «Администрировании».
 */
import { useState } from 'react';
import type { RoomSummary, ServerInfo, TableCategory } from '@mt/protocol';
import type { SignedPackage } from '@mt/verification';
import { t, type TKey } from '../i18n';
import { api } from '../lib/api';
import { client, useApp } from '../net/client';
import { cr, crn, pts, secondsLeft, STAGE_TITLES, useNow } from '../lib/format';
import { Link } from '../lib/router';
import { Avatar, pct, signedCr, toneOf } from './kit';
import { Rules } from './Rules';
import { VerifyDialog } from './VerifyDialog';

/** Ключи названий категорий: сервер присылает русское название, клиент переводит по id. */
const CAT_TITLE_KEYS: Record<string, TKey> = { n10: 'lobby.cat.n10', n50: 'lobby.cat.n50', n200: 'lobby.cat.n200' };

/** Название категории столов на текущем языке; неизвестная категория — название сервера. */
export function catTitle(cat: { id: string; title: string }): string {
  const key = CAT_TITLE_KEYS[cat.id];
  return key ? t(key) : cat.title;
}

export function Lobby() {
  const info = useApp((s) => s.info);
  const lobby = useApp((s) => s.lobby);
  const profile = useApp((s) => s.profile);
  const [rules, setRules] = useState(false);
  const [verify, setVerify] = useState<SignedPackage | null | undefined>(undefined);
  const now = useNow(500);
  if (!info || !profile) return null;

  const playing = lobby.reduce((a, r) => a + r.seated, 0);
  const st = profile.stats;

  return (
    <div className="lobby">
      <header className="lobby-head">
        <div>
          <h1>{t('lobby.title')}</h1>
          <p className="muted-line">{t('lobby.intro')}</p>
        </div>
        <dl className="head-stats">
          <div>
            <dt>{t('lobby.balance')}</dt>
            <dd className="mono">{cr(profile.balance)}</dd>
          </div>
          <div>
            <dt>{t('lobby.atTables')}</dt>
            <dd className="mono">{playing}</dd>
          </div>
          <div>
            <dt>{t('lobby.openTables')}</dt>
            <dd className="mono">{lobby.length}</dd>
          </div>
        </dl>
      </header>

      <div className="lobby-grid">
        <section className="lobby-main">
          <div className="section-head">
            <h2>{t('lobby.tables')}</h2>
            <button className="btn tiny ghost" onClick={() => setRules(true)}>
              {t('lobby.rulesBtn')}
            </button>
          </div>
          <div className="categories">
            {info.categories.map((cat) => (
              <CategoryCard key={cat.id} cat={cat} info={info} rooms={lobby.filter((r) => r.categoryId === cat.id)} balance={profile.balance} seatedRoomId={profile.seatedRoomId} now={now} />
            ))}
          </div>

          <div className="section-head">
            <h2>{t('lobby.flowTitle')}</h2>
          </div>
          <ol className="flow">
            <li>
              <b>{t('lobby.flow.prep')}</b>
              <span>{t('lobby.flow.prepText')}</span>
            </li>
            <li>
              <b>{t('lobby.flow.round1')}</b>
              <span>{t('lobby.flow.round1Text')}</span>
            </li>
            <li>
              <b>{t('lobby.flow.round2')}</b>
              <span>{t('lobby.flow.round2Text')}</span>
            </li>
            <li>
              <b>{t('lobby.flow.round3')}</b>
              <span>{t('lobby.flow.round3Text')}</span>
            </li>
            <li>
              <b>{t('lobby.flow.target')}</b>
              <span>{t('lobby.flow.targetText')}</span>
            </li>
            <li>
              <b>{t('lobby.flow.settle')}</b>
              <span>{t('lobby.flow.settleText')}</span>
            </li>
          </ol>
        </section>

        <aside className="lobby-side">
          <section className="panel profile-card">
            <div className="pc-head">
              <Avatar id={profile.id} name={profile.name} size={44} />
              <div>
                <b>{profile.name}</b>
                <span className="mono">{cr(profile.balance)}</span>
              </div>
            </div>
            <p className="muted-line small">{t('lobby.creditsNote')}</p>
            {profile.canRestore && (
              <button className="btn wide" onClick={() => void api.resetBalance().catch((e: Error) => client.toast('error', e.message))}>
                {t('lobby.restore', { amount: info.startingBalance })}
              </button>
            )}
            <dl className="stat-tiles">
              <div>
                <dt>{t('lobby.stat.games')}</dt>
                <dd className="mono">{st.games}</dd>
              </div>
              <div>
                <dt>{t('lobby.stat.winRate')}</dt>
                <dd className="mono">{pct(st.winRate)}</dd>
              </div>
              <div>
                <dt>{t('lobby.stat.net')}</dt>
                <dd className={`mono ${toneOf(st.net)}`}>{signedCr(st.net)}</dd>
              </div>
              <div>
                <dt>{t('lobby.stat.avgError')}</dt>
                <dd className="mono">{st.avgError === null ? '—' : pts(st.avgError)}</dd>
              </div>
            </dl>
            <Link to="/profile" className="btn wide">
              {t('lobby.profileLink')}
            </Link>
          </section>

          <section className="panel">
            <div className="section-head compact">
              <h3>{t('lobby.verifyTitle')}</h3>
            </div>
            <p className="muted-line">
              {t('lobby.verifyTextBefore')} <code>npm run verify</code> {t('lobby.verifyTextAfter')}
            </p>
            <button className="btn wide" onClick={() => setVerify(null)}>
              {t('lobby.verifyBtn')}
            </button>
          </section>
        </aside>
      </div>
      {rules && <Rules info={info} onClose={() => setRules(false)} />}
      {verify !== undefined && <VerifyDialog initial={verify} onClose={() => setVerify(undefined)} />}
    </div>
  );
}

function CategoryCard(props: { cat: TableCategory; info: ServerInfo; rooms: RoomSummary[]; balance: number; seatedRoomId: string | null; now: number }) {
  const { cat, info, rooms, now } = props;
  const enough = props.balance >= cat.ante;
  const seatedHere = rooms.some((r) => r.roomId === props.seatedRoomId);
  const seated = rooms.reduce((a, r) => a + r.seated, 0);
  const cap = info.commissionCapAntesPerRound;
  return (
    <article className={`panel category ${seatedHere ? 'mine' : ''}`}>
      <header>
        <span className="cat-label">{catTitle(cat)}</span>
        <span className="cat-ante mono">
          {crn(cat.ante)}
          <small> {t('common.creditsUnit')}</small>
        </span>
        <span className="muted-line">{t('lobby.cat.anteN')}</span>
      </header>
      <dl className="cat-meta">
        <div>
          <dt>{t('lobby.cat.players')}</dt>
          <dd>
            {cat.minPlayers}–{cat.maxPlayers}
          </dd>
        </div>
        <div>
          <dt>{t('lobby.cat.rounds')}</dt>
          <dd>{t('lobby.cat.roundsValue', { s: Math.round(info.stageMs / 1000) })}</dd>
        </div>
        <div>
          <dt>{t('lobby.cat.commission')}</dt>
          <dd>
            {info.commissionBps / 100} %{cap ? ` · ≤ ${cap}N` : ''}
          </dd>
        </div>
      </dl>
      <div className="cat-rooms">
        <div className="cat-rooms-head">
          <span>{t('lobby.cat.tables')}</span>
          <span className="dim">{rooms.length ? t('lobby.cat.roomsCount', { rooms: rooms.length, seated }) : t('lobby.cat.noOpen')}</span>
        </div>
        {rooms.length === 0 ? (
          <p className="muted-line">{t('lobby.cat.newTable')}</p>
        ) : (
          <ul className="room-list">
            {rooms.map((r) => (
              <RoomRow key={r.roomId} r={r} now={now} mine={r.roomId === props.seatedRoomId} />
            ))}
          </ul>
        )}
      </div>
      <button className="btn primary wide" disabled={!enough} onClick={() => client.join(cat.id)}>
        {seatedHere ? t('lobby.cat.return') : t('lobby.cat.sit')}
      </button>
      {!enough && <p className="notice warn">{t('lobby.notEnough')}</p>}
    </article>
  );
}

function RoomRow({ r, now, mine }: { r: RoomSummary; now: number; mine: boolean }) {
  const left = secondsLeft(r.nextStartAt, now);
  const status =
    r.status === 'playing'
      ? r.stage
        ? STAGE_TITLES[r.stage]
        : t('lobby.room.playing')
      : r.status === 'countdown'
        ? t('lobby.room.startIn', { n: left ?? 0 })
        : r.status === 'results'
          ? t('lobby.room.settling')
          : t('lobby.room.ready', { ready: r.ready, min: r.minPlayers });
  return (
    <li className={`room-row ${mine ? 'mine' : ''}`}>
      <span className="seat-dots" aria-label={t('lobby.room.taken', { seated: r.seated, max: r.maxPlayers })}>
        {Array.from({ length: r.maxPlayers }, (_, i) => (
          <i key={i} className={i < r.ready ? 'ready' : i < r.seated ? 'taken' : ''} />
        ))}
      </span>
      <span className={`room-status ${r.status}`}>{status}</span>
      <button className="btn tiny ghost" onClick={() => client.watch(r.roomId)}>
        {mine ? t('lobby.room.open') : t('lobby.room.watch')}
      </button>
    </li>
  );
}
